import { db } from '@/config/firebase';
// Simplified imports
import { doc, getDoc, Timestamp } from 'firebase/firestore';

// Define the expected structure of the oldValuesMap
type OldValuesMap = Record<string, string>;

// Define the structure of the individual entries within the 'entries' array
// Only include fields needed by the functions in this file
interface LedgerEntryForService {
    date?: Timestamp; // Needed for sorting and returning date
    oldValuesMap?: OldValuesMap | null; // Needed for oldValuesMap fetch
    // Add other fields ONLY if needed for future logic within THIS function
    [key: string]: any;
}

// Define the structure of the document holding the entries array
interface LedgerGroupDoc {
    entries?: LedgerEntryForService[]; // Use the simplified local interface
    originalDataElement?: string;
}

/**
 * Fetches the oldValuesMap from the most recent 'Standard ASIN Review'
 * entry within the document in the pmt2_ledger collection.
 *
 * @param companyName The name of the company.
 * @param asin The ASIN (product ID).
 * @returns A promise that resolves to the OldValuesMap object or null if not found or error occurs.
 */
export const fetchLatestOldValuesMap = async (
    companyName: string,
    asin: string
): Promise<OldValuesMap | null> => {
    if (!companyName || !asin) {
        return null;
    }

    try {
        const docId = 'Standard ASIN Review'; // Document ID matches Data Element
        const reviewDocRef = doc(db, 'pmt2_ledger', companyName, asin, docId);
        const docSnap = await getDoc(reviewDocRef);

        if (!docSnap.exists()) {
            return null;
        }

        const docData = docSnap.data() as LedgerGroupDoc;
        const entries = docData.entries;

        if (!entries || !Array.isArray(entries) || entries.length === 0) {
            return null;
        }

        const sortedEntries = entries
            .filter(entry => entry.date instanceof Timestamp)
            .sort((a, b) => (b.date as Timestamp).toMillis() - (a.date as Timestamp).toMillis());

        if (sortedEntries.length === 0) {
             return null;
        }

        const latestEntry = sortedEntries[0];
        return latestEntry.oldValuesMap ?? null;

    } catch (error) {
        return null;
    }
};

/**
 * Fetches the date of the most recent entry for a specific Data Element
 * within the pmt2_ledger collection for a given company and ASIN.
 *
 * @param companyName The name of the company.
 * @param asin The ASIN (product ID).
 * @param dataElement The specific Data Element (document ID) to fetch (e.g., "Gross Profit Override").
 * @returns A promise that resolves to the Timestamp object or null if not found or error occurs.
 */
export const fetchLatestLedgerEntryDate = async (
    companyName: string,
    asin: string,
    dataElement: string // e.g., "Gross Profit Override"
): Promise<Timestamp | null> => {
     if (!companyName || !asin || !dataElement) {
        return null;
    }
    // console.log(`[OldValuesService] Fetching date for ${companyName}/${asin}/${dataElement}`); // Optional debug log

    try {
        // Path to the document named after the Data Element
        const entryDocRef = doc(db, 'pmt2_ledger', companyName, asin, dataElement);
        const docSnap = await getDoc(entryDocRef);

        if (!docSnap.exists()) {
            // console.log(`[OldValuesService] Document ${dataElement} not found at ${entryDocRef.path}.`); // Optional debug log
            return null;
        }

        const docData = docSnap.data() as LedgerGroupDoc;
        const entries = docData.entries;

        if (!entries || !Array.isArray(entries) || entries.length === 0) {
            // console.log(`[OldValuesService] No 'entries' array found or array is empty in document ${dataElement}.`); // Optional debug log
            return null;
        }

        // Sort the entries array by date (descending) to find the latest
        const sortedEntries = entries
            .filter(entry => entry.date instanceof Timestamp) // Filter out entries without valid Timestamps
            .sort((a, b) => (b.date as Timestamp).toMillis() - (a.date as Timestamp).toMillis());

        if (sortedEntries.length === 0) {
             // console.log(`[OldValuesService] No entries with valid dates found for ${dataElement}.`); // Optional debug log
             return null;
        }

        const latestEntry = sortedEntries[0];
        // console.log(`[OldValuesService] Latest entry date for ${dataElement}:`, latestEntry.date); // Optional debug log

        // Return the date field from the latest entry
        return latestEntry.date ?? null;

    } catch (error) {
        return null; // Return null on error
    }
};